import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link, useSearchParams, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT, LangSelect } from "../lib/i18n";
import { Field } from "../components/Common";
import { ClinicContact } from "../components/ClinicContact";
import { usePublicBranding } from "./public/PublicFrame";
import { AutoLogo } from "../components/AppLogo";

export default function Login() {
  const { user, setUser } = useApp();
  const { t } = useT();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [params] = useSearchParams();
  const [showCreate, setShowCreate] = useState(params.get("contact") === "1");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(params.get("expired") === "1" ? "You were signed out. Please sign in again to continue." : "");
  const { slug } = useParams();
  const [clinic, setClinic] = useState(null);
  useEffect(() => {
    if (slug) api.get(`/public/c/${slug}/hub`).then((r) => setClinic(r.data)).catch(() => setClinic(false));
  }, [slug]);
  usePublicBranding(clinic || null);
  if (user) return <Navigate to="/app" replace />;
  if (slug && clinic === null) return <div className="min-h-screen" />;
  if (slug && clinic === false) return (
    <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="clinic-login-not-found">
      <div className="space-y-3"><p className="font-semibold">Clinic not found.</p><Link to="/login" className="text-primary hover:underline">{t("back_login")}</Link></div>
    </div>
  );
  const brand = clinic?.branding || {};
  const clinicName = brand.clinic_name || clinic?.name;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (mode === "login") {
        await api.post("/auth/login", { email, password });
        let { data } = await api.get("/auth/me");
        const hq = !slug && (data.linked || []).find((l) => l.is_hq);
        if (hq) {
          await api.post("/auth/switch", { user_id: hq.id });
          data = (await api.get("/auth/me")).data;
        }
        if (slug) localStorage.setItem("bw_login_slug", slug);
        else localStorage.removeItem("bw_login_slug");
        setUser(data);
        nav("/app");
      } else {
        await api.post("/auth/forgot-password", { email });
        toast.success(t("reset_sent"));
        setMode("login");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const fields = (
    <>
      <Field label={t("email")}>
        <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      {mode === "login" && (
        <Field label={t("password")}>
          <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      )}
      {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
      <Button data-testid="login-submit-button" disabled={busy} className="w-full rounded-full h-11">
        {mode === "login" ? t("sign_in") : t("send_reset")}
      </Button>
      <button type="button" data-testid="toggle-forgot-btn" onClick={() => setMode(mode === "login" ? "forgot" : "login")}
        className="text-sm text-primary hover:underline">{mode === "login" ? t("forgot") : t("back_login")}</button>
      <div className="border-t pt-4 text-sm text-center space-y-2">
        <Link to={clinic ? `/p/${slug}?mode=register` : "/patient"} data-testid="patient-signup-link" className="block rounded-full border border-primary/40 py-2 text-primary font-medium hover:bg-primary/5">{t("create_account")}</Link>
        {!clinic && <p className="text-xs text-muted-foreground">{t("clinic_contact_q")} <button type="button" data-testid="clinic-contact-link" onClick={() => setShowCreate(true)} className="text-primary hover:underline">{t("clinic_contact")}</button></p>}
      </div>
    </>
  );

  return (
    <div className="relative min-h-screen overflow-hidden grain flex items-center justify-center p-5 sm:p-10" data-testid="login-centered">
      <div aria-hidden className="pointer-events-none absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-primary/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -right-24 h-[32rem] w-[32rem] rounded-full bg-accent/25 blur-3xl" />
      <div className="relative z-10 w-full max-w-md fade-up">
        <div className="flex justify-end mb-2"><LangSelect testId="login-lang-toggle" /></div>
        <form onSubmit={submit} className="rounded-[1.75rem] border bg-card/85 backdrop-blur-xl shadow-[0_30px_80px_-30px_hsl(var(--primary)/0.35)] p-7 sm:p-9 space-y-5" data-testid="login-form">
          <div className="text-center space-y-3">
            {clinic
              ? (brand.logo ? <AutoLogo src={brand.logo} alt={clinicName} testId="clinic-login-logo" className="h-14 sm:h-16 max-w-[220px] mx-auto" />
                : <p data-testid="clinic-login-name" className="text-2xl font-bold">{clinicName}</p>)
              : <img src="/brand/bloomwell-logo.png" alt="Bloomwell" data-testid="login-full-logo" className="h-11 sm:h-12 w-auto object-contain mx-auto" />}
            <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{clinic ? "Team area" : "Multi-location · Multi-currency"}</p>
            <h1 className="text-2xl font-bold leading-tight" data-testid="login-tagline">{clinic ? "Welcome back to the team." : "Every booking, client and payment. One calm place."}</h1>
          </div>
          <div className="text-center">
            <h2 className="text-lg font-semibold">{mode === "login" ? (clinic ? "Staff login" : t("login_title")) : t("forgot")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{clinic ? `Sign in to ${clinicName}.` : t("login_sub")}</p>
          </div>
          {fields}
        </form>
        <p className="text-center text-xs text-muted-foreground mt-6">© {new Date().getFullYear()} {clinic ? `${clinicName} · Powered by Bloomwell` : "Bloomwell"}</p>
      </div>
      <ClinicContact open={showCreate} onOpenChange={setShowCreate} />
    </div>
  );
}
